import { _ } from "../../core/polyglot/polyglot.js";
import AccountingCoordinator from "../shared/documentInfrastructure/AccountingCoordinator.js";
import ClosingWrapperWidget from "../../core/widgets/ClosingWrapperWidget/ClosingWrapperWidget.js";
import ForecastCellInfoWidget from "./widgets/ForecastCellInfoWidget/ForecastCellInfoWidget.js";
import ForecastDisplaySettingsWidget from "./widgets/ForecastDisplaySettingsWidget/ForecastDisplaySettingsWidget.js";

export const currentTabKey = "currentTab";

// We store in localStorage an object whose keys are forecast
// report ids and values are booleans. A value of `true` means
// that the help wizard for this report has already been
// viewed.
const localStorageId = "forecast-editor-help-viewed";

/**
 * Model holding all the parts of a forecasting document, its widgets
 * and its state.  It handles communication between all of these by
 * registering to events and dispatching them via message sends.
 */
export default class ForecastEditorDocumentCoordinator extends AccountingCoordinator {
	async setup({ currentTabId }) {
		await super.setup(...arguments);

		this._drawer.reset();

		this._documentState
			.getReportsAsList()
			.forEach((report) => report.expandRowsUpToDefaultLevel());

		history.replaceState({ currentTab: currentTabId }, "");

		this._title.setSaveSpaceMode(this._documentState.isInSaveSpaceMode());
	}

	shouldDocumentFitViewport() {
		return true;
	}

	triggerPostShowWork() {
		this._startHelpWizard();
	}

	handleOnDrawerWidthChange() {
		this._content.handleWidthChange();
	}

	_onPopState({ [currentTabKey]: currentTabId }) {
		this._documentState.setCurrentReportFromCurrentTab(currentTabId);

		this._content.setCurrentTab({
			report: this._documentState.getCurrentReport(),
		});
	}

	_setupDocumentStateCallbacks() {
		return {
			onForecastReportUpdated: this._onForecastReportUpdated.bind(this),
			setCurrentTabParameter: this._setCurrentTabParameter.bind(this),
			onForecastPeriodUpdated: this._onForecastPeriodUpdated.bind(this),
		};
	}

	_setupContentCallbacks() {
		return {
			onCellClick: this._handleOnCellClick.bind(this),
			onSettingsButtonClick: this._handleOnSettingsButtonClick.bind(this),
			hideDrawer: () => this._hideDrawer(),
			getForecastUrl: this._getForecastViewerUrl.bind(this),
		};
	}

	/**
	 * Automatically start the help wizard if the user hasn't seen it
	 * yet for the current report.
	 */
	_startHelpWizard() {
		const helpViewed = this._getHelpViewed();

		if (!this._shouldShowHelp(helpViewed)) return;

		this._markHelpAsViewed(helpViewed);
		this._title.startHelpWizard();
	}

	_getHelpViewed() {
		const helpViewedString =
			window.localStorage.getItem(localStorageId) || "{}";

		let helpViewed;

		try {
			helpViewed = JSON.parse(helpViewedString);
		} catch {
			helpViewed = {};
		}

		return helpViewed;
	}

	_shouldShowHelp(helpViewed) {
		const currentReportId = this._documentState.getCurrentReport()?.getId();

		return currentReportId && !helpViewed[currentReportId];
	}

	_markHelpAsViewed(helpViewed) {
		const currentReportId = this._documentState.getCurrentReport()?.getId();
		helpViewed[currentReportId] = true;
		window.localStorage.setItem(localStorageId, JSON.stringify(helpViewed));
	}

	_handleOnCellClick({ row, column }) {
		this._showDrawerWith(
			new ClosingWrapperWidget({
				content: this._makeForecastCellInfoWidget({ row, column }),
				onClose: () => this._drawer.hide(),
			}),
		);

		// Make sure the cell is completely visible:
		this._content.scrollToSelectedCell();
	}

	_makeForecastCellInfoWidget({ row, column }) {
		return new ForecastCellInfoWidget({
			documentState: this._documentState,
			row,
			column,
			onReportAndCellSelectionRequest:
				this._content.selectReportAndCell.bind(this._content),
			manualJournalEntryTransactionsValidator:
				this._validateManualJournalEntryTransactions.bind(this),
		});
	}

	_validateManualJournalEntryTransactions({
		collector,
		context: { transactions },
	}) {
		let atLeastOneTransactionFound = false;
		let atLeastOneAccountIsBalanceSheetAccount = false;

		transactions.forEach((transaction) => {
			const hasValue = !!transaction.getValue();

			atLeastOneTransactionFound ||= hasValue;

			atLeastOneAccountIsBalanceSheetAccount ||=
				hasValue && this._isBalanceAccount(transaction.getAccount());
		});

		collector.addErrorIf(
			!atLeastOneTransactionFound,
			_("No transaction with a value found"),
		);

		collector.addErrorIf(
			!atLeastOneAccountIsBalanceSheetAccount,
			_("At least one account must be a Balance Sheet account"),
		);
	}

	_isBalanceAccount(account) {
		const report = this._documentState.getBalanceSheet();
		return report.hasRowForAccountNumber(account.getNumber());
	}

	_setCurrentTabParameter(currentTabId) {
		this._startHelpWizard();
		this._setUrlParameters({ [currentTabKey]: currentTabId });
		this._title.update();
	}

	_onForecastReportUpdated() {
		this._content.updateCurrentReport();
		if (this._drawer.isVisible()) {
			this._drawer.update();
		}
	}

	_onForecastPeriodUpdated(period) {
		this._updateContentReports();
		const updatedCurrentReport = this._content.getCurrentReport();
		this._documentState.setCurrentReport(updatedCurrentReport);
		this._title.setPeriod(period);
	}

	_updateContentReports() {
		const originalSettings = this._documentState.getReportDisplaySettings();

		this._content.update();

		this._content.getAllReports().forEach((report, index) => {
			const settings = originalSettings[index];
			this._displaySettingsFormOnChange({ report, settings });
		});
	}

	_handleOnSettingsButtonClick({ report }) {
		// we don't have to handle the case where the drawer is already open,
		// as when the drawer opens it hides the settings button
		this._showDrawerWith(
			new ClosingWrapperWidget({
				content: this._makeForecastDisplaySettingsWidget({ report }),
				onClose: () => this._drawer.hide(),
			}),
		);
	}

	_onSaveSpaceModeChange(value) {
		this._documentState.setSaveSpaceMode(value);
		this._title.setSaveSpaceMode(value);
		this._content.updateSaveSpaceMode();
	}

	_displaySettingsFormOnChange({ report, settings }) {
		report.setDisplaySettings(settings);
		report.expandRowsUpToDefaultLevel();

		this._documentState.computeReport({ report });

		this._content.updateCurrentReport();
	}

	_makeForecastDisplaySettingsWidget({ report }) {
		return new ForecastDisplaySettingsWidget({
			isInSaveSpaceMode: this._documentState.isInSaveSpaceMode(),
			reportDisplaySettings: report.getDisplaySettings(),
			onChange: (settings) =>
				this._displaySettingsFormOnChange({ report, settings }),
			onSaveSpaceModeChange: (value) =>
				this._onSaveSpaceModeChange(value),
		});
	}

	_getForecastViewerUrl({ forecast }) {
		return this._router.linkTo("forecast-viewer/#forecastId", {
			forecastId: forecast.getId(),
		});
	}
}
